import React from "react";

import Typography from "@mui/material/Typography";

import useUpdateClips from "@app/analytics/Home/hooks/useUpdateClips";
import type { ActionItemFeedItem } from "@app/analytics/Home/types";
import { useAuthenticated } from "@app/common/auth/useAuth";
import { showToast } from "@app/common/hooks/useToasts";
import type {
  ActionItemClipStatus,
  UpdateClipOperation,
} from "@app/common/types";

import Stack, { Row } from "@rui/Stack";

import BaseHomeFeedItemLayout from "./BaseHomeFeedItemLayout";
import HomeFeedItemVideoClip from "./HomeFeedItemVideoClip";
import { Content, Footer } from "./styles";
import ActionItemStatusButton from "../ActionItemStatusButton";

interface ConnectedActionItemFeedItemProps extends ActionItemFeedItem {
  onClick: (id: string) => void;
}

interface ActionItemFeedItemProps extends ConnectedActionItemFeedItemProps {
  isAssignedToCurrentUser: boolean;
  onUpdateStatus: (status: ActionItemClipStatus) => void;
}

const ActionItem = ({
  isAssignedToCurrentUser,
  onClick,
  onUpdateStatus,
  ...item
}: ActionItemFeedItemProps) => {
  return (
    <BaseHomeFeedItemLayout {...item} onClick={onClick}>
      <Stack>
        <Row spacing={1.5} paddingBottom={1} justifyContent="space-between">
          <Stack>
            <Content spacing={1} type={item.type}>
              {isAssignedToCurrentUser && (
                <Typography variant="bodySecondaryMed">
                  Assigned to you
                </Typography>
              )}
              <Typography variant="bodySecondaryReg">{item.text}</Typography>
            </Content>
          </Stack>
          {item.videoClip && (
            <HomeFeedItemVideoClip
              entityId={item.entityId}
              session={item.session}
              videoClip={item.videoClip}
            />
          )}
        </Row>
        {item.data.canEdit && (
          <Footer>
            <ActionItemStatusButton
              status={item.data.status}
              onChangeStatus={onUpdateStatus}
            />
          </Footer>
        )}
      </Stack>
    </BaseHomeFeedItemLayout>
  );
};

const ConnectedActionItem = (props: ConnectedActionItemFeedItemProps) => {
  const { user } = useAuthenticated();
  const updateClip = useUpdateClips();

  const handleUpdateStatus = (status: ActionItemClipStatus) => {
    const operation: UpdateClipOperation = {
      id: props.entityId,
      sessionId: props.session.id,
      status,
      type: "action_item",
    };

    updateClip.mutate(operation, {
      onSuccess: () => {
        if (status === "user_reject") {
          showToast({
            severity: "success",
            message: "Your feedback has been recorded.",
          });
        }
      },
      onError: () => {
        showToast({
          severity: "error",
          message: "Something went wrong and your changes could not be saved.",
        });
      },
    });
  };

  const isAssignedToCurrentUser = user.id === props.data.ownerUserId;

  return (
    <ActionItem
      {...props}
      isAssignedToCurrentUser={isAssignedToCurrentUser}
      onUpdateStatus={handleUpdateStatus}
    />
  );
};

export { ActionItem };
export default ConnectedActionItem;
